import { SidebarChat, useSidebarChat } from '@platform/chat';
import type {
  AdditionDetail,
  BundleConflictResponse,
  ConflictDetail,
  CreateBundleRequest,
  DraftEntry,
  GetProfileRequest,
  PublishBundleResponse,
  SlotPromptSet,
  UpsertEntryRequest,
} from '@shared/types/profile';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@shared/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useArchiveBundleMutation,
  useCreateBundleMutation,
  useDeleteEntryMutation,
  useGetProfileQuery,
  useListBundlesQuery,
  usePublishBundleMutation,
  useUpsertEntryMutation,
} from '../../store/api/rpcSlice';
import { getOrCreateProfileEditorConvID } from './profileEditorConvId';

// Profile-specific bundle selection storage key
const getStorageKey = (profileSlug: string) => `profileEditor.selectedBundleId.${profileSlug}`;

/**
 * Format user display as "Name (email)" if name is available, otherwise just email.
 * Falls back to user ID if neither name nor email is available.
 */
function formatUserDisplay(
  name: string | undefined,
  email: string | undefined,
  userId: string
): string {
  if (name && email) {
    return `${name} (${email})`;
  }
  if (email) {
    return email;
  }
  if (name) {
    return name;
  }
  return userId;
}

export default function ProfileDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(() => {
    if (!slug) return null;
    const stored = localStorage.getItem(getStorageKey(slug));
    return stored || null;
  });
  const [activeTab, setActiveTab] = useState(0);
  const [editingEntry, setEditingEntry] = useState<DraftEntry | null>(null);
  const [editText, setEditText] = useState('');
  const [editChangelog, setEditChangelog] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bundleName, setBundleName] = useState('');
  const [bundleDescription, setBundleDescription] = useState('');
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState<BundleConflictResponse | null>(null);

  // Fetch bundles
  const { data: bundlesData } = useListBundlesQuery({});
  const bundles = bundlesData?.bundles || [];
  const [createBundle] = useCreateBundleMutation();

  // Update bundle selection when profile slug changes
  useEffect(() => {
    if (slug) {
      const stored = localStorage.getItem(getStorageKey(slug));
      setSelectedBundleId(stored || null);
    }
  }, [slug]);

  // Validate stored bundle ID on load and when bundles change
  useEffect(() => {
    if (!slug) return;

    // If we have a selected bundle but bundles have loaded
    if (selectedBundleId && bundles.length > 0) {
      const bundle = bundles.find((b) => b.id === selectedBundleId);
      // Clear if bundle doesn't exist or is archived
      if (!bundle || bundle.archived_at) {
        localStorage.removeItem(getStorageKey(slug));
        setSelectedBundleId(null);
      }
    }
  }, [slug, selectedBundleId, bundles]);

  // Fetch profile detail
  const request: GetProfileRequest = useMemo(
    () => ({
      slug: slug || '',
      bundle_id: selectedBundleId || undefined,
    }),
    [slug, selectedBundleId]
  );
  const { data: profileData, isLoading, refetch } = useGetProfileQuery(request);
  const profile = profileData?.profile;
  const draftEntries = profileData?.draft_entries || [];
  const sourceVersions = profileData?.source_versions || [];

  // Helper function to check if a prompt has a conflict
  // Match by slug since prompt IDs change with each version
  const hasConflict = useCallback(
    (promptSlug: string | undefined, currentVersion: number | undefined) => {
      if (!promptSlug || !currentVersion || !selectedBundleId || sourceVersions.length === 0)
        return false;
      const sourceVer = sourceVersions.find((v) => v.slug === promptSlug);
      return sourceVer && sourceVer.version < currentVersion;
    },
    [sourceVersions, selectedBundleId]
  );

  const [upsertEntry] = useUpsertEntryMutation();
  const [deleteEntry] = useDeleteEntryMutation();
  const [publishBundle] = usePublishBundleMutation();
  const [archiveBundle, { isLoading: isArchiving }] = useArchiveBundleMutation();

  // Sidebar chat for testing
  // Only pass draft bundle ID if it's valid (exists in bundles list)
  const validBundleId =
    selectedBundleId && bundles.find((b) => b.id === selectedBundleId && !b.archived_at)
      ? selectedBundleId
      : undefined;

  // Create a unique conversation ID that changes when the bundle changes.
  // Persist only for the current page runtime (window-global) so HMR keeps it,
  // but a full page reload resets it (no sessionStorage persistence).
  // The generated ID is always UUID-shaped to match backend conversation ID contracts.
  const convId = useMemo(() => {
    const bundleID = validBundleId || 'no-bundle';
    const runtime =
      typeof window === 'undefined' ? undefined : (window as unknown as Record<string, unknown>);
    return getOrCreateProfileEditorConvID({
      slug: slug || 'default',
      bundleID,
      runtime,
      generateUUID: () => crypto.randomUUID(),
    });
  }, [slug, validBundleId]);

  const chat = useSidebarChat({
    convId,
    profile: slug || 'default',
    draftBundleId: validBundleId,
  });

  const handleEditPrompt = useCallback(
    (slot: SlotPromptSet, _mwName: string) => {
      if (!selectedBundleId) {
        alert('Please select a draft bundle first');
        return;
      }
      const resolvedPrompt = slot.resolved_prompt;
      if (!resolvedPrompt) {
        alert('No prompt to edit');
        return;
      }
      // Check if draft entry already exists
      const existingEntry = draftEntries.find(
        (e) => e.slug === slot.resolved_slug && e.scope_type === 'global'
      );
      if (existingEntry) {
        setEditingEntry(existingEntry);
        setEditText(existingEntry.text);
        setEditChangelog(existingEntry.changelog || '');
      } else {
        // Create new draft entry
        setEditingEntry({
          id: '',
          bundle_id: selectedBundleId,
          slug: slot.resolved_slug,
          scope_type: 'global',
          text: resolvedPrompt.text,
          source_prompt_id: resolvedPrompt.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        setEditText(resolvedPrompt.text);
        setEditChangelog('');
      }
    },
    [selectedBundleId, draftEntries]
  );

  const handleSaveEntry = useCallback(async () => {
    if (!selectedBundleId || !editingEntry) return;
    try {
      const req: UpsertEntryRequest = {
        bundle_id: selectedBundleId,
        slug: editingEntry.slug,
        scope_type: editingEntry.scope_type,
        text: editText,
        changelog: editChangelog || undefined,
        source_prompt_id: editingEntry.source_prompt_id,
      };
      await upsertEntry(req).unwrap();
      setEditingEntry(null);
      setEditText('');
      setEditChangelog('');
      refetch();
    } catch (err) {
      console.error('Failed to save entry:', err);
      alert('Failed to save draft entry');
    }
  }, [selectedBundleId, editingEntry, editText, editChangelog, upsertEntry, refetch]);

  const handleDeleteEntry = useCallback(
    async (entry: DraftEntry) => {
      if (!confirm(`Delete draft entry for ${entry.slug}?`)) return;
      try {
        await deleteEntry({
          bundle_id: entry.bundle_id,
          entry_id: entry.id,
        }).unwrap();
        refetch();
      } catch (err) {
        console.error('Failed to delete entry:', err);
        alert('Failed to delete draft entry');
      }
    },
    [deleteEntry, refetch]
  );

  const handlePublishBundle = useCallback(
    async (force = false) => {
      if (!selectedBundleId || !slug) return;

      try {
        const result = await publishBundle({ bundle_id: selectedBundleId, force }).unwrap();
        // Check for conflicts in response
        if (
          result.conflicts &&
          (result.conflicts.conflicts?.length > 0 || result.conflicts.additions?.length > 0)
        ) {
          setConflictData(result.conflicts);
          setConflictDialogOpen(true);
          return;
        }
        // Success - no conflicts
        alert('Bundle published successfully!');
        localStorage.removeItem(getStorageKey(slug));
        setSelectedBundleId(null);
        refetch();
      } catch (err: any) {
        // Check if it's a conflict response (409 status)
        if (err?.status === 409 || err?.status === 'FETCH_ERROR') {
          // Try to parse conflict data from response
          try {
            const conflictResponse = err?.data as PublishBundleResponse;
            if (conflictResponse?.conflicts) {
              setConflictData(conflictResponse.conflicts);
              setConflictDialogOpen(true);
              return;
            }
          } catch {
            // Fall through to error message
          }
        }
        const errorMsg = err?.data?.error || err?.message || 'Failed to publish bundle';
        alert(`Failed to publish bundle: ${errorMsg}`);
      }
    },
    [selectedBundleId, slug, publishBundle, refetch]
  );

  const handleForcePublish = useCallback(() => {
    setConflictDialogOpen(false);
    handlePublishBundle(true);
  }, [handlePublishBundle]);

  const handleCreateBundle = useCallback(async () => {
    if (!bundleName.trim() || !slug) return;
    try {
      const req: CreateBundleRequest = {
        name: bundleName.trim(),
        description: bundleDescription.trim() || undefined,
      };
      const result = await createBundle(req).unwrap();
      setSelectedBundleId(result.bundle.id);
      localStorage.setItem(getStorageKey(slug), result.bundle.id);
      setCreateDialogOpen(false);
      setBundleName('');
      setBundleDescription('');
      refetch();
    } catch (err) {
      console.error('Failed to create bundle:', err);
      alert('Failed to create bundle');
    }
  }, [bundleName, bundleDescription, slug, createBundle, refetch]);

  const handleArchiveBundle = useCallback(async () => {
    if (!selectedBundleId || !slug) return;
    if (!confirm('Archive this bundle? You will stop using these drafts for testing.')) return;
    try {
      await archiveBundle({ id: selectedBundleId }).unwrap();
      localStorage.removeItem(getStorageKey(slug));
      setSelectedBundleId(null);
      alert('Bundle archived.');
      refetch();
    } catch (err: any) {
      const errorMsg = err?.data?.error || err?.message || 'Failed to archive bundle';
      alert(errorMsg);
    }
  }, [archiveBundle, selectedBundleId, slug, refetch]);

  const handleBundleChange = useCallback(
    (bundleId: string | null) => {
      if (!slug) return;
      setSelectedBundleId(bundleId);
      if (bundleId) {
        localStorage.setItem(getStorageKey(slug), bundleId);
      } else {
        localStorage.removeItem(getStorageKey(slug));
      }
    },
    [slug]
  );

  if (isLoading || !profile) {
    return <LinearProgress />;
  }

  const activeBundle = bundles.find((b) => b.id === selectedBundleId);
  const basePrompt = profile.base_prompt;

  // Leave headroom for the layout header/back bar so the chat input stays visible without scrolling.
  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 80px)', overflow: 'hidden', minHeight: 0 }}>
      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, minHeight: 0 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate('/admin/profiles')}>←</IconButton>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {profile.slug}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Base Prompt: {profile.base_prompt_slug}
          </Typography>

          {/* Bundle Selector */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Draft Bundle:
              </Typography>
              {bundles.length > 0 ? (
                <>
                  <select
                    value={selectedBundleId || ''}
                    onChange={(e) => handleBundleChange(e.target.value || null)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '14px',
                    }}
                  >
                    <option value="">None (View Only)</option>
                    {bundles.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.entry_count} edits)
                      </option>
                    ))}
                  </select>
                  <Button variant="outlined" size="small" onClick={() => setCreateDialogOpen(true)}>
                    + New Bundle
                  </Button>
                  {activeBundle && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => handlePublishBundle(false)}
                        disabled={activeBundle.entry_count === 0}
                      >
                        Publish Bundle
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleArchiveBundle}
                        disabled={isArchiving}
                      >
                        Archive Bundle
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    No draft bundles yet. Click 'New Bundle' to start editing prompts.
                  </Typography>
                  <Button variant="outlined" size="small" onClick={() => setCreateDialogOpen(true)}>
                    + New Bundle
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="Base Prompt" />
          <Tab label="Middleware Stack" />
          {selectedBundleId && <Tab label={`Draft Entries (${draftEntries.length})`} />}
        </Tabs>

        {/* Base Prompt Tab */}
        {activeTab === 0 && (
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Base Prompt</Typography>
                  {basePrompt && hasConflict(basePrompt.slug, basePrompt.version) && (
                    <Tooltip
                      title={`Based on v${sourceVersions.find((v) => v.slug === basePrompt.slug)?.version}, current is v${basePrompt.version}`}
                    >
                      <span>
                        <Chip label="⚠️ Conflict" color="warning" size="small" />
                      </span>
                    </Tooltip>
                  )}
                </Box>
                {basePrompt && (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!selectedBundleId}
                    onClick={() => {
                      if (!selectedBundleId || !basePrompt) return;
                      const existing = draftEntries.find(
                        (e) => e.slug === profile.base_prompt_slug && e.scope_type === 'global'
                      );
                      if (existing) {
                        setEditingEntry(existing);
                        setEditText(existing.text);
                        setEditChangelog(existing.changelog || '');
                      } else {
                        setEditingEntry({
                          id: '',
                          bundle_id: selectedBundleId,
                          slug: profile.base_prompt_slug,
                          scope_type: 'global',
                          text: basePrompt.text,
                          source_prompt_id: basePrompt.id,
                          created_at: new Date().toISOString(),
                          updated_at: new Date().toISOString(),
                        });
                        setEditText(basePrompt.text);
                        setEditChangelog('');
                      }
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Box>
              {basePrompt ? (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                  >
                    {(() => {
                      // Check if there's a draft entry for the base prompt
                      const draftEntry = draftEntries.find(
                        (e) => e.slug === profile.base_prompt_slug && e.scope_type === 'global'
                      );
                      return draftEntry ? draftEntry.text : basePrompt.text;
                    })()}
                  </Typography>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No base prompt found
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Middleware Stack Tab */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {profile.middleware_prompt_views?.map((mwView) => (
              <Card key={mwView.name}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {mwView.name}
                  </Typography>
                  {mwView.slots.map((slot) => (
                    <Box key={slot.slot} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            Slot: {slot.slot} ({slot.resolved_source})
                          </Typography>
                          {slot.resolved_prompt &&
                            hasConflict(
                              slot.resolved_prompt.slug,
                              slot.resolved_prompt.version
                            ) && (
                              <Tooltip
                                title={`Based on v${sourceVersions.find((v) => v.slug === slot.resolved_prompt?.slug)?.version}, current is v${slot.resolved_prompt.version}`}
                              >
                                <span>
                                  <Chip label="⚠️" color="warning" size="small" />
                                </span>
                              </Tooltip>
                            )}
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleEditPrompt(slot, mwView.name)}
                          disabled={!selectedBundleId}
                        >
                          Edit
                        </Button>
                      </Box>
                      {slot.resolved_prompt ? (
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography
                            variant="body2"
                            component="pre"
                            sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                          >
                            {(() => {
                              // Check if there's a draft entry for this slot
                              const draftEntry = draftEntries.find(
                                (e) => e.slug === slot.resolved_slug && e.scope_type === 'global'
                              );
                              return draftEntry ? draftEntry.text : slot.resolved_prompt.text;
                            })()}
                          </Typography>
                        </Paper>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No prompt found
                        </Typography>
                      )}
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Draft Entries Tab */}
        {activeTab === 2 && selectedBundleId && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {draftEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No draft entries in this bundle
              </Typography>
            ) : (
              draftEntries.map((entry) => (
                <Card key={entry.id}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="h6">{entry.slug}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Scope: {entry.scope_type}
                          {entry.org_id && ` • Org: ${entry.org_id}`}
                          {entry.person_id && ` • Person: ${entry.person_id}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setEditingEntry(entry);
                            setEditText(entry.text);
                            setEditChangelog(entry.changelog || '');
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDeleteEntry(entry)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                      >
                        {entry.text}
                      </Typography>
                    </Paper>
                    {entry.changelog && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        Changelog: {entry.changelog}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        )}
      </Box>

      {/* Sidebar Chat for Testing */}
      <Box
        sx={{
          width: 420,
          borderLeft: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
          flexShrink: 0,
        }}
      >
        <SidebarChat
          storageId="test-chat"
          entities={chat.entities}
          convId={chat.convId}
          onSendMessage={chat.sendMessage}
          isLoading={chat.isStreaming}
          placeholder="Test your prompt changes..."
          height="100%"
          showHeader={false}
        />
      </Box>

      {/* Edit Entry Dialog */}
      <Dialog
        open={!!editingEntry}
        onClose={() => setEditingEntry(null)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Edit Draft Entry</DialogTitle>
        <DialogContent>
          {editingEntry && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Slug: {editingEntry.slug}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={15}
                label="Prompt Text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Changelog (optional)"
                placeholder="Describe what changed..."
                value={editChangelog}
                onChange={(e) => setEditChangelog(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingEntry(null)}>Cancel</Button>
          <Button onClick={handleSaveEntry} variant="contained" disabled={!editText.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conflict Detection Dialog */}
      <Dialog
        open={conflictDialogOpen}
        onClose={() => setConflictDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>⚠️ Prompt Conflicts Detected</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Some prompts have been modified since this bundle was created. Publishing will overwrite
            the newer changes.
          </Typography>

          {conflictData?.conflicts && conflictData.conflicts.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: 'warning.main' }}>
                Version Conflicts ({conflictData.conflicts.length})
              </Typography>
              {conflictData.conflicts.map((conflict: ConflictDetail) => (
                <Paper
                  key={conflict.prompt_id}
                  sx={{ p: 2, mb: 1, bgcolor: 'warning.light', opacity: 0.8 }}
                >
                  <Typography variant="body2">
                    <strong>Prompt:</strong> {conflict.slug || conflict.prompt_id}
                  </Typography>
                  <Typography variant="body2">
                    Bundle version: {conflict.bundle_version} → Current version:{' '}
                    {conflict.current_version}
                  </Typography>
                  {conflict.updated_by && (
                    <Typography variant="body2" color="text.secondary">
                      Updated by:{' '}
                      {formatUserDisplay(
                        conflict.updated_by_name,
                        conflict.updated_by_email,
                        conflict.updated_by
                      )}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Updated at: {new Date(conflict.updated_at).toLocaleString()}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}

          {conflictData?.additions && conflictData.additions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: 'warning.main' }}>
                Prompts Modified Since Bundle Created ({conflictData.additions.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                These prompts were updated after you started working on this bundle.
              </Typography>
              {conflictData.additions.map((addition: AdditionDetail) => (
                <Paper
                  key={addition.prompt_id}
                  sx={{ p: 2, mb: 1, bgcolor: 'warning.light', opacity: 0.6 }}
                >
                  <Typography variant="body2">
                    <strong>Prompt:</strong> {addition.slug || addition.prompt_id}
                  </Typography>
                  <Typography variant="body2">Current version: {addition.version}</Typography>
                  {addition.added_by && (
                    <Typography variant="body2" color="text.secondary">
                      Modified by:{' '}
                      {formatUserDisplay(
                        addition.added_by_name,
                        addition.added_by_email,
                        addition.added_by
                      )}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Modified at: {new Date(addition.added_at).toLocaleString()}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}

          <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 'bold' }}>
            ⚠️ Warning: Proceeding will overwrite the newer changes shown above.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Recommended: Click "Review and Update" to manually merge the latest changes into your
            bundle before publishing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleForcePublish} variant="outlined" color="error">
            Proceed Anyway (Overwrite)
          </Button>
          <Button
            onClick={() => setConflictDialogOpen(false)}
            variant="contained"
            color="primary"
            autoFocus
          >
            Review and Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Bundle Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Create Draft Bundle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Bundle Name"
            placeholder="e.g., coach-tweaks"
            fullWidth
            required
            value={bundleName}
            onChange={(e) => setBundleName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            placeholder="Optional description of what this bundle changes..."
            fullWidth
            multiline
            rows={3}
            value={bundleDescription}
            onChange={(e) => setBundleDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateBundle} variant="contained" disabled={!bundleName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
